import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Trash2, ChevronLeft, Clock, Calendar } from "lucide-react";
import { getSavedPosts } from "./PublicBlogPost";

const SAVED_KEY = "raf_blog_saved_v1";

const CAT_COLORS = {
  "Dependência Química": "bg-rose-100 text-rose-800 border-rose-200",
  "Saúde Mental": "bg-violet-100 text-violet-800 border-violet-200",
  "Tratamento": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Clínicas de Recuperação": "bg-sky-100 text-sky-800 border-sky-200",
  "Profissionais RAF": "bg-amber-100 text-amber-800 border-amber-200",
};

export default function PublicBlogSaved() {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    setItems(getSavedPosts());
  }, []);

  useEffect(() => {
    load();
    document.title = "Salvos · Blog RAF Direct";
  }, [load]);

  const remove = (slug) => {
    const next = getSavedPosts().filter(s => s.slug !== slug);
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(next)); } catch { /* silent */ }
    setItems(next);
    toast.success("Removido dos salvos");
  };

  const clearAll = () => {
    if (!window.confirm("Remover TODOS os artigos salvos?")) return;
    try { localStorage.setItem(SAVED_KEY, JSON.stringify([])); } catch { /* silent */ }
    setItems([]);
    toast.success("Lista limpa");
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10" data-testid="public-blog-saved">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0B2B62] mb-3" data-testid="blog-saved-back">
          <ChevronLeft className="w-3.5 h-3.5"/> Voltar pro Blog
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-slate-500 flex items-center gap-1"><Bookmark className="w-3 h-3"/> Blog</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0B2B62]">Salvos para ler depois</h1>
            <p className="text-sm text-slate-600 mt-1">{items.length} artigo{items.length !== 1 ? "s" : ""} guardado{items.length !== 1 ? "s" : ""} neste navegador.</p>
          </div>
          {items.length > 0 && (
            <button onClick={clearAll} data-testid="blog-saved-clear-all" className="text-xs text-red-600 hover:text-red-800 underline">Limpar tudo</button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white border border-dashed border-slate-200" data-testid="blog-saved-empty">
            <Bookmark className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-slate-500 text-sm">Você ainda não salvou nenhum artigo.</div>
            <Link to="/blog" className="inline-block mt-4 px-4 py-2 rounded-lg bg-[#0B2B62] text-white text-sm font-bold hover:bg-[#1E4FB8] transition">Explorar o Blog</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(it => {
              const catColor = CAT_COLORS[it.category] || "bg-slate-100 text-slate-700 border-slate-200";
              const savedAt = it.saved_at ? new Date(it.saved_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : "";
              const fallback = `https://picsum.photos/seed/${it.slug}/800/450`;
              return (
                <div key={it.slug} className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition p-3 flex gap-3 items-center" data-testid={`blog-saved-item-${it.slug}`}>
                  <Link to={`/blog/${it.slug}`} className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                    <img src={it.hero_image || fallback} alt={it.title} loading="lazy"
                         onError={e=>{ if(e.target.dataset.fbk!=='1'){e.target.dataset.fbk='1'; e.target.src=fallback;} }}
                         className="w-full h-full object-cover"/>
                  </Link>
                  <Link to={`/blog/${it.slug}`} className="min-w-0 flex-1 group">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${catColor}`}>{it.category}</span>
                      <span className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5"/>{it.read_minutes || 5} min</span>
                      {savedAt && <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><Calendar className="w-2.5 h-2.5"/>Salvo {savedAt}</span>}
                    </div>
                    <div className="text-sm sm:text-base font-bold text-[#0B2B62] leading-tight line-clamp-2 group-hover:text-[#1E4FB8] transition">{it.title}</div>
                    {it.excerpt && <div className="text-xs text-slate-600 line-clamp-1 mt-1">{it.excerpt}</div>}
                  </Link>
                  <button onClick={()=>remove(it.slug)} data-testid={`blog-saved-remove-${it.slug}`} title="Remover" className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition shrink-0">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
